import { useEffect, useState } from 'react';
import Modal from './Modal';
import { Loader2, StickyNote, Trash2, X } from 'lucide-react';
import { useApi } from '../api/client';

// Chronological notes for a contact, unified across Spark + Discovery
// (decision 1A). Opened from the Notes button on either grid; `callType`
// tags newly-added notes with the originating grid for the badge.

export interface NotesTarget {
  contactId: number;
  contactName: string;
  callType: 'legacy_chat' | 'discovery_call';
}

interface Note {
  id: number;
  body: string;
  call_type: string | null;
  created_at: string | null;
}

const CALL_TYPE_LABEL: Record<string, string> = {
  legacy_chat: 'Spark',
  discovery_call: 'Discovery',
};

export default function NotesModal({
  target,
  onClose,
  onChanged,
}: {
  target: NotesTarget | null;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const apiFetch = useApi();
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [tick, setTick] = useState(0);

  const contactId = target?.contactId ?? null;

  useEffect(() => {
    if (contactId == null) {
      setNotes(null);
      return;
    }
    let cancelled = false;
    setNotes(null);
    setError(null);
    apiFetch(`/contacts/${contactId}/notes`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d: { notes: Note[] }) => {
        if (!cancelled) setNotes(d.notes);
      })
      .catch((e) => {
        if (!cancelled) setError(String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [contactId, apiFetch, tick]);

  if (!target) return null;

  async function addNote(e: React.FormEvent) {
    e.preventDefault();
    if (!target || saving) return;
    const body = draft.trim();
    if (!body) return;
    setSaving(true);
    setError(null);
    try {
      const r = await apiFetch(`/contacts/${target.contactId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ body, call_type: target.callType }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDraft('');
      setTick((t) => t + 1);
      onChanged?.();
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  }

  async function removeNote(id: number) {
    if (!target) return;
    if (!confirm('Delete this note?')) return;
    setDeletingId(id);
    setError(null);
    try {
      const r = await apiFetch(`/contacts/${target.contactId}/notes/${id}`, {
        method: 'DELETE',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setTick((t) => t + 1);
      onChanged?.();
    } catch (err) {
      setError(String(err));
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <Modal
      onClose={onClose}
      cardClassName="flex max-h-[85vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-2xl"
    >
          <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-brand-mint to-brand-sky px-5 py-3 text-brand-forest">
            <div className="flex items-center gap-2 font-display font-black">
              <StickyNote className="h-5 w-5" />
              Notes — {target.contactName}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 transition hover:bg-brand-forest/10"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {error && (
              <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            {notes == null ? (
              <div className="flex items-center gap-2 text-sm text-brand-sage">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            ) : notes.length === 0 ? (
              <div className="rounded-lg border border-dashed border-brand-sage/40 bg-brand-paper px-3 py-6 text-center text-sm text-brand-sage">
                No notes yet. Add the first one below.
              </div>
            ) : (
              <ul className="space-y-2">
                {notes.map((n) => (
                  <li
                    key={n.id}
                    className="rounded-lg border border-brand-sage/25 bg-brand-paper px-3 py-2"
                  >
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-xs text-brand-sage">
                        <span>
                          {n.created_at
                            ? new Date(n.created_at).toLocaleDateString()
                            : '—'}
                        </span>
                        {n.call_type && CALL_TYPE_LABEL[n.call_type] && (
                          <span className="rounded-full bg-brand-mint-100 px-2 py-0.5 font-bold text-brand-forest">
                            {CALL_TYPE_LABEL[n.call_type]}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeNote(n.id)}
                        disabled={deletingId === n.id}
                        title="Delete note"
                        className="shrink-0 rounded p-1 text-brand-sage transition hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                      >
                        {deletingId === n.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                    <div className="whitespace-pre-wrap text-sm text-brand-forest">
                      {n.body}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <form onSubmit={addNote} className="border-t border-brand-sage/20 px-5 py-4">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              placeholder="Add a note…"
              className="w-full resize-y rounded-md border border-brand-sage/40 bg-white px-3 py-2 text-sm text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
            />
            <div className="mt-2 flex justify-end">
              <button
                type="submit"
                disabled={saving || !draft.trim()}
                className="flex items-center gap-2 rounded-xl bg-brand-lime px-4 py-2 text-sm font-display font-black text-brand-forest shadow-sm transition hover:bg-brand-lime-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {saving ? 'Adding…' : 'Add note'}
              </button>
            </div>
          </form>
    </Modal>
  );
}